import React, { useState, useEffect, useCallback } from 'react';
import { ref as dbRef, onValue } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { database, functions } from '../firebaseConfig';
import { CREDIT_PLANS, EMAIL_PLAN_LIMITS, resolveMembershipPlanId } from '../config/creditPlans';
import {
  computeNextDistributionIso,
  formatCreditCycleDate,
  resolveNextCreditCycle,
} from '../utils/creditCycle';
import './MonthlyCredits.css';

const EMAIL_PLANS = {
  standard: { label: 'Standard', limit: EMAIL_PLAN_LIMITS.standard, color: '#6b7280' },
  premium: { label: 'Premium', limit: EMAIL_PLAN_LIMITS.premium, color: '#3b82f6' },
  unlimited: { label: 'Unlimited', limit: EMAIL_PLAN_LIMITS.unlimited, color: '#10b981' },
};

const ACADEMY_TIERS = [
  { key: 'team', label: 'Team', icon: 'fa fa-users' },
  { key: 'standard', label: 'Standard', icon: 'fa fa-book' },
  { key: 'premium', label: 'Premium', icon: 'fa fa-gem' },
];

function MonthlyCredits() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCredits, setUserCredits] = useState({});
  const [bonusCredits, setBonusCredits] = useState({});
  const [userPlans, setUserPlans] = useState({});
  const [userData, setUserData] = useState({});
  const [selectedPlan, setSelectedPlan] = useState('none');
  const [includeAcademy, setIncludeAcademy] = useState(false);
  const [selectedAcademyTiers, setSelectedAcademyTiers] = useState({ team: false, standard: false, premium: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDistributing, setIsDistributing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const auth = getAuth();
  const currentUser = auth.currentUser;
  const selectedEmailPlan = CREDIT_PLANS[selectedPlan]?.emailPlan || 'standard';

  useEffect(() => {
    if (!currentUser) return;

    setIsLoading(true);
    const usersRef = dbRef(database, 'Users');
    const unsubscribe = onValue(usersRef, async (snapshot) => {
      const usersData = snapshot.val();
      if (!usersData) {
        setUsers([]);
        setIsLoading(false);
        return;
      }

      const usersArray = Object.entries(usersData).map(([id, data]) => ({
        id,
        email: data.email || 'No email',
        name: data.name || data.user_name || '',
        companyName: data.companyName || data.company_name || '',
        isPremium: data.isPremium || false,
        accountType: data.accountType || 'unknown',
      }));

      const userDataMap = {};
      Object.entries(usersData).forEach(([id, data]) => {
        userDataMap[id] = {
          hasSalesGod: data.hasSalesGod || false,
          isPremium: data.isPremium || false,
          hasAcademy: data.hasAcademy || false,
          emailPlan: data.emailPlan || 'standard',
          dailyEmailLimit: data.dailyEmailLimit || 42,
          academyTiers: data.academyTiers || { team: false, standard: false, premium: false },
          // Fallback for the plan badge: some records only have the legacy
          // `Users/{uid}/membershipPlan` flag and no `World/{uid}/creditPlan`.
          // resolveMembershipPlanId() checks this second — see below.
          membershipPlan: data.membershipPlan || null,
        };
      });
      setUserData(userDataMap);

      usersArray.sort((a, b) => a.email.localeCompare(b.email));
      setUsers(usersArray);
      setFilteredUsers(usersArray);

      await loadAllCreditsAndPlans(usersArray);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const loadAllCreditsAndPlans = async (usersList) => {
    try {
      const getAllCreditsAdminFn = httpsCallable(functions, 'getAllCreditsAdmin');
      const userIds = usersList.map(u => u.id);
      const result = await getAllCreditsAdminFn({ userIds });
      setUserCredits(result.data.credits || {});
      setBonusCredits(result.data.bonusCredits || {});
      setUserPlans(result.data.plans || {});
    } catch (err) {
      console.warn('[Memberships] Failed to load credits via admin function:', err.message || err);
      setUserCredits({});
      setUserPlans({});
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredUsers(users.filter(user =>
        user.email.toLowerCase().includes(query) ||
        user.name.toLowerCase().includes(query) ||
        user.companyName.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, users]);

  const handleSelectUser = useCallback((user) => {
    setSelectedUser(user);
    setSuccessMessage('');
    setErrorMessage('');

    // Use the shared resolver so legacy aliases (standard/premium/unlimited),
    // records without `isActive`, and users with only the fallback flag on
    // `Users/{uid}/membershipPlan` all resolve to the correct plan id.
    const resolvedPlanId = resolveMembershipPlanId(userPlans[user.id], userData[user.id]);
    setSelectedPlan(resolvedPlanId || 'none');
    setIncludeAcademy(!!userData[user.id]?.hasAcademy);
    setSelectedAcademyTiers(userData[user.id]?.academyTiers || { team: false, standard: false, premium: false });
  }, [userPlans, userData]);

  const handleSavePlan = async () => {
    if (!selectedUser || !currentUser || selectedPlan === 'none') return;

    setIsSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const userId = selectedUser.id;
      const planToSend = selectedPlan === 'deactivate' ? null : selectedPlan;
      const updatePlanFn = httpsCallable(functions, 'updateCreditPlanAdmin');
      const result = await updatePlanFn({
        targetUserId: userId,
        plan: planToSend,
        emailPlan: selectedEmailPlan,
        includeAcademy,
        academyTiers: selectedAcademyTiers,
      });

      if (result.data.action === 'deactivated') {
        setUserPlans(prev => ({
          ...prev,
          [userId]: { ...prev[userId], isActive: false, deactivatedAt: new Date().toISOString() },
        }));
        setUserData(prev => ({
          ...prev,
          [userId]: { ...prev[userId], hasSalesGod: false, isPremium: false, hasAcademy: false, emailPlan: 'standard', dailyEmailLimit: 42, academyTiers: { team: false, standard: false, premium: false }, membershipPlan: null },
        }));
        setSuccessMessage(`Membership deactivated for ${selectedUser.email}`);
      } else {
        const planData = result.data.plan;
        const forfeited = result.data.forfeited || 0;
        const newBalance = result.data.newBalance;
        const allowance = result.data.allowance;
        setUserPlans(prev => ({
          ...prev,
          [userId]: { ...prev[userId], ...planData },
        }));
        if (typeof newBalance === 'number') {
          setUserCredits(prev => ({ ...prev, [userId]: newBalance }));
        }
        setUserData(prev => ({
          ...prev,
          [userId]: {
            ...prev[userId],
            hasSalesGod: true,
            isPremium: true,
            emailPlan: selectedEmailPlan,
            dailyEmailLimit: EMAIL_PLANS[selectedEmailPlan]?.limit || 42,
            hasAcademy: includeAcademy,
            academyTiers: includeAcademy ? selectedAcademyTiers : { team: false, standard: false, premium: false },
            membershipPlan: selectedPlan,
          },
        }));
        const planConfig = CREDIT_PLANS[selectedPlan];
        const emailLabel = EMAIL_PLANS[selectedEmailPlan]?.label || 'Standard';
        const creditNote = `wallet reset to ${(allowance ?? planConfig.monthlyCredits).toLocaleString()} credits`
          + (forfeited > 0 ? ` (${forfeited.toLocaleString()} leftover forfeited)` : '');
        setSuccessMessage(`${planConfig.label} membership activated for ${selectedUser.email} — ${emailLabel} email plan, ${creditNote} (Balance: ${(newBalance ?? 0).toLocaleString()}). Next cycle in one month.`);
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      const msg = error?.message || '';
      if (msg.includes('Admin access required') || msg.includes('permission-denied')) {
        setErrorMessage('Admin access required. Please make sure your account has admin privileges.');
      } else {
        setErrorMessage(msg || 'Failed to save plan. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDistributeNow = async () => {
    if (!selectedUser || !currentUser) return;

    const plan = userPlans[selectedUser.id];
    if (!plan || !plan.isActive) {
      setErrorMessage('User has no active plan.');
      return;
    }

    setIsDistributing(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const distributeCreditsNow = httpsCallable(functions, 'distributeCreditsNow');
      const result = await distributeCreditsNow({ targetUserId: selectedUser.id });
      const { previousBalance, added, newBalance, forfeited, nextDistribution } = result.data;

      setUserCredits(prev => ({
        ...prev,
        [selectedUser.id]: newBalance,
      }));

      const now = new Date().toISOString();
      setUserPlans(prev => ({
        ...prev,
        [selectedUser.id]: {
          ...prev[selectedUser.id],
          lastDistribution: now,
          nextDistribution: nextDistribution || computeNextDistributionIso(),
        },
      }));

      // A refill RESETS the balance to the monthly allowance, it does not add to
      // it — saying "distributed X" alone would read as prev + X.
      setSuccessMessage(
        `Reset ${selectedUser.email} to the ${added.toLocaleString()} credit monthly allowance. `
        + `Balance: ${previousBalance.toLocaleString()} → ${newBalance.toLocaleString()}`
        + (forfeited > 0 ? ` (${forfeited.toLocaleString()} unused credits forfeited)` : '')
      );
    } catch (error) {
      console.error('Error distributing credits:', error);
      setErrorMessage(error.message || 'Failed to distribute credits.');
    } finally {
      setIsDistributing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return formatCreditCycleDate(new Date(dateStr), { withYear: true });
    } catch {
      return '—';
    }
  };

  const formatNextDistribution = (plan) => {
    const next = resolveNextCreditCycle(plan);
    if (!next) return '—';
    return formatCreditCycleDate(next, { withYear: true });
  };

  // Count what actually renders as active — a plan is "active" if the
  // resolver would return an id for it (handles legacy aliases, missing
  // `isActive`, and the `Users/{uid}/membershipPlan` fallback).
  const activePlansCount = users.reduce((count, u) => (
    resolveMembershipPlanId(userPlans[u.id], userData[u.id]) ? count + 1 : count
  ), 0);

  if (isLoading) {
    return (
      <div className="monthly-credits-container">
        <div className="monthly-credits-loading">
          <i className="fa fa-spinner fa-spin"></i>
          <span>Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="monthly-credits-container">
      {/* Header */}
      <div className="monthly-credits-header">
        <div className="monthly-credits-header-title">
          <h2>Memberships</h2>
          <p>Activate SalesGod accounts with credit plans, email packages and Academy access. Activating a plan resets the wallet to that month&apos;s allowance — leftovers do not stack. The next refill is one month later.</p>
        </div>
        <div className="monthly-credits-header-stats">
          <div className="monthly-credits-stat-box">
            <span className="monthly-credits-stat-value">{users.length}</span>
            <span className="monthly-credits-stat-label">Total Users</span>
          </div>
          <div className="monthly-credits-stat-box">
            <span className="monthly-credits-stat-value">{activePlansCount}</span>
            <span className="monthly-credits-stat-label">Active Plans</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="monthly-credits-message monthly-credits-error">
          <i className="fa fa-exclamation-circle"></i>
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage('')}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="monthly-credits-message monthly-credits-success">
          <i className="fa fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage('')}>×</button>
        </div>
      )}

      <div className="monthly-credits-content">
        {/* Users Panel */}
        <div className="monthly-credits-users-panel">
          <div className="monthly-credits-panel-header">
            <h3>Select User</h3>
            <div className="monthly-credits-search-box">
              <i className="fa fa-search"></i>
              <input
                type="text"
                placeholder="Search by email, name or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="monthly-credits-clear-search" onClick={() => setSearchQuery('')}>
                  <i className="fa fa-times"></i>
                </button>
              )}
            </div>
          </div>

          <div className="monthly-credits-users-list">
            {filteredUsers.length === 0 ? (
              <div className="monthly-credits-no-users">
                <i className="fa fa-user-slash"></i>
                <span>No users found</span>
              </div>
            ) : (
              filteredUsers.map(user => {
                const plan = userPlans[user.id];
                const planKey = resolveMembershipPlanId(plan, userData[user.id]);
                const planConfig = planKey ? CREDIT_PLANS[planKey] : null;

                return (
                  <div
                    key={user.id}
                    className={`monthly-credits-user-item ${selectedUser?.id === user.id ? 'monthly-credits-user-selected' : ''}`}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="monthly-credits-user-avatar">
                      {user.email.charAt(0).toUpperCase()}
                    </div>
                    <div className="monthly-credits-user-info">
                      <span className="monthly-credits-user-email">{user.email}</span>
                      {user.companyName && (
                        <span className="monthly-credits-user-company">{user.companyName}</span>
                      )}
                    </div>
                    <div className="monthly-credits-user-badges">
                      {userData[user.id]?.hasSalesGod && (
                        <span className="monthly-credits-status-dot monthly-credits-dot-salesgod" title="SalesGod Active" />
                      )}
                      {planConfig ? (
                        <span
                          className="monthly-credits-plan-badge"
                          style={{ background: planConfig.gradient }}
                        >
                          {planConfig.label}
                        </span>
                      ) : (
                        <span className="monthly-credits-plan-badge monthly-credits-plan-badge-none">
                          No Plan
                        </span>
                      )}
                      <div className="monthly-credits-balance-badge" title={(bonusCredits[user.id] || 0) > 0 ? `${userCredits[user.id] || 0} monthly, ${bonusCredits[user.id]} extra kept after the reset` : 'Monthly credits'}>
                        {userCredits[user.id] || 0}
                        {(bonusCredits[user.id] || 0) > 0 ? ` + ${bonusCredits[user.id]}` : ''}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Plan Panel */}
        <div className="monthly-credits-plan-panel">
          <div className="monthly-credits-panel-header">
            <h3>Membership Setup</h3>
          </div>

          {selectedUser ? (
            <div className="monthly-credits-plan-form">
              {/* Selected User Card */}
              <div className="monthly-credits-selected-user-card">
                <div className="monthly-credits-selected-avatar">
                  {selectedUser.email.charAt(0).toUpperCase()}
                </div>
                <div className="monthly-credits-selected-details">
                  <span className="monthly-credits-selected-email">{selectedUser.email}</span>
                  {selectedUser.companyName && (
                    <span className="monthly-credits-selected-company">{selectedUser.companyName}</span>
                  )}
                  <div className="monthly-credits-current-info">
                    Monthly: <strong>{(userCredits[selectedUser.id] || 0).toLocaleString()}</strong>
                    {(bonusCredits[selectedUser.id] || 0) > 0 && (
                      <span> · Extra: <strong>{bonusCredits[selectedUser.id].toLocaleString()}</strong> (kept after the reset)</span>
                    )}
                  </div>
                  <div className="monthly-credits-status-tags">
                    <span className={`monthly-credits-status-tag ${userData[selectedUser.id]?.hasSalesGod ? 'monthly-credits-tag-active' : 'monthly-credits-tag-inactive'}`}>
                      <i className="fa fa-rocket"></i> SalesGod
                    </span>
                    <span className={`monthly-credits-status-tag ${userData[selectedUser.id]?.isPremium ? 'monthly-credits-tag-active' : 'monthly-credits-tag-inactive'}`}>
                      <i className="fa fa-crown"></i> Premium
                    </span>
                    <span className={`monthly-credits-status-tag ${userData[selectedUser.id]?.hasAcademy ? 'monthly-credits-tag-active' : 'monthly-credits-tag-inactive'}`}>
                      <i className="fa fa-graduation-cap"></i> Academy
                    </span>
                    <span className={`monthly-credits-status-tag monthly-credits-tag-email-${userData[selectedUser.id]?.emailPlan || 'standard'}`}>
                      <i className="fa fa-envelope"></i> {EMAIL_PLANS[userData[selectedUser.id]?.emailPlan || 'standard']?.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Current Plan Info */}
              {userPlans[selectedUser.id] && userPlans[selectedUser.id].plan && (
                <div className="monthly-credits-current-plan-info">
                  <div className={`monthly-credits-current-plan-row ${userPlans[selectedUser.id].isActive ? 'monthly-credits-active-row' : 'monthly-credits-inactive-row'}`}>
                    <span>Status</span>
                    <span>{userPlans[selectedUser.id].isActive ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className="monthly-credits-current-plan-row">
                    <span>Last Distribution</span>
                    <span>{formatDate(userPlans[selectedUser.id].lastDistribution)}</span>
                  </div>
                  <div className="monthly-credits-current-plan-row">
                    <span>Next Distribution</span>
                    <span>{formatNextDistribution(userPlans[selectedUser.id])}</span>
                  </div>
                </div>
              )}

              {/* Plan Selection */}
              <span className="monthly-credits-plan-section-label">Select Plan</span>
              <div className="monthly-credits-plan-options">
                {Object.entries(CREDIT_PLANS).map(([key, plan]) => {
                  const f = plan.features;
                  return (
                    <div
                      key={key}
                      className={`monthly-credits-plan-card ${selectedPlan === key ? 'monthly-credits-plan-card-active' : ''}`}
                      style={{ borderColor: selectedPlan === key ? plan.color : undefined, color: plan.color }}
                      onClick={() => setSelectedPlan(key)}
                    >
                      <div className="monthly-credits-plan-card-header" style={selectedPlan === key ? { background: plan.gradient } : {}}>
                        <span className="monthly-credits-plan-card-name" style={{ color: selectedPlan === key ? '#ffffff' : plan.color }}>{plan.label}</span>
                        <span className="monthly-credits-plan-card-credits" style={selectedPlan === key ? { background: 'rgba(255,255,255,0.2)', color: '#ffffff' } : {}}>{plan.monthlyCredits.toLocaleString()} credits</span>
                      </div>
                      <ul className="monthly-credits-feature-list">
                        <li><i className="fa fa-envelope" style={{ color: plan.color }}></i> <strong>{f.emailsPerDay}</strong> emails / day</li>
                        <li><i className="fa fa-broom" style={{ color: plan.color }}></i> <strong>{f.leadsPerMonth === -1 ? 'Unlimited' : f.leadsPerMonth.toLocaleString()}</strong> lead cleans / mo</li>
                        <li><i className="fa fa-search-plus" style={{ color: plan.color }}></i> <strong>{f.deepScansPerMonth.toLocaleString()}</strong> deep scans / mo</li>
                        <li className={!f.hyperIntelligence ? 'monthly-credits-feature-disabled' : ''}>
                          <i className="fa fa-brain" style={{ color: f.hyperIntelligence ? plan.color : undefined }}></i> Hyper-Intelligence
                          {f.hyperIntelligence && <span className="monthly-credits-feature-check" style={{ color: plan.color }}>✓</span>}
                          {!f.hyperIntelligence && <span className="monthly-credits-feature-na">—</span>}
                        </li>
                        <li className={!f.academy ? 'monthly-credits-feature-disabled' : ''}>
                          <i className="fa fa-graduation-cap" style={{ color: f.academy ? plan.color : undefined }}></i> Academy
                          {f.academy ? <span className="monthly-credits-feature-tier" style={selectedPlan === key ? { background: plan.gradient } : {}}>{f.academy}</span> : <span className="monthly-credits-feature-na">—</span>}
                        </li>
                      </ul>
                    </div>
                  );
                })}

                {/* Deactivate Option */}
                <div
                  className={`monthly-credits-plan-card monthly-credits-plan-card-deactivate ${selectedPlan === 'deactivate' ? 'monthly-credits-plan-card-active' : ''}`}
                  style={selectedPlan === 'deactivate' ? { borderColor: '#ef4444' } : {}}
                  onClick={() => setSelectedPlan('deactivate')}
                >
                  <div className="monthly-credits-plan-card-header">
                    <span className="monthly-credits-plan-card-name" style={{ color: '#ef4444' }}>
                      <i className="fa fa-power-off"></i> Deactivate
                    </span>
                  </div>
                  <p className="monthly-credits-deactivate-desc">Remove membership, SalesGod, Premium & reset email plan</p>
                </div>
              </div>

              {/* Academy Toggle + Tiers */}
              <span className="monthly-credits-plan-section-label">Academy Access</span>
              <div className="monthly-credits-academy-section">
                <div
                  className={`monthly-credits-academy-toggle ${includeAcademy ? 'monthly-credits-academy-toggle-active' : ''}`}
                  onClick={() => setIncludeAcademy(prev => !prev)}
                >
                  <div className={`monthly-credits-toggle-switch ${includeAcademy ? 'monthly-credits-toggle-on' : ''}`}>
                    <div className="monthly-credits-toggle-knob" />
                  </div>
                  <div className="monthly-credits-academy-toggle-info">
                    <span className="monthly-credits-academy-toggle-label">Include Academy</span>
                    <span className="monthly-credits-academy-toggle-desc">Grant access to learning resources</span>
                  </div>
                </div>

                {includeAcademy && (
                  <div className="monthly-credits-academy-tiers">
                    {ACADEMY_TIERS.map((tier) => (
                      <div
                        key={tier.key}
                        className={`monthly-credits-tier-chip ${selectedAcademyTiers[tier.key] ? 'monthly-credits-tier-chip-active' : ''}`}
                        onClick={() => setSelectedAcademyTiers(prev => ({ ...prev, [tier.key]: !prev[tier.key] }))}
                      >
                        <i className={tier.icon}></i>
                        <span>{tier.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="monthly-credits-actions">
                <button
                  className={`monthly-credits-save-btn ${selectedPlan === 'deactivate' ? 'monthly-credits-deactivate-btn' : ''}`}
                  onClick={handleSavePlan}
                  disabled={isSaving || selectedPlan === 'none'}
                >
                  {isSaving ? (
                    <><i className="fa fa-spinner fa-spin"></i> Saving...</>
                  ) : selectedPlan === 'deactivate' ? (
                    <><i className="fa fa-power-off"></i> Deactivate Membership</>
                  ) : (
                    <><i className="fa fa-rocket"></i> Activate Membership</>
                  )}
                </button>

                {userPlans[selectedUser.id]?.isActive && (
                  <button
                    className="monthly-credits-distribute-btn"
                    onClick={handleDistributeNow}
                    disabled={isDistributing}
                  >
                    {isDistributing ? (
                      <><i className="fa fa-spinner fa-spin"></i> Distributing...</>
                    ) : (
                      <><i className="fa fa-bolt"></i> Distribute Now</>
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="monthly-credits-no-selection">
              <div className="monthly-credits-no-selection-icon"><i className="fa fa-user-circle"></i></div>
              <h4>Select a User</h4>
              <p>Choose a user from the list to manage their membership and SalesGod access</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MonthlyCredits;
